import { VagaTypeKey } from "../../services/stationState.types";

export interface VagaMeta {
  /** Nome legível derivado da chave (ex.: "Elevador 1", "Vaga 01"). */
  name: string;
  /** Código curto (ex.: "EL-01", "V-01"). */
  code: string;
  /** Tipo inferido pelo prefixo da chave — fallback quando `type` vem null. */
  typeFromKey: VagaTypeKey | null;
}

const PREFIXES: Array<{
  match: RegExp;
  name: string;
  code: string;
  type: VagaTypeKey;
}> = [
  { match: /^elevador/i, name: "Elevador", code: "EL", type: "Operacao" },
  { match: /^super/i, name: "Supermercado", code: "SM", type: "Supermercado" },
  { match: /^extra/i, name: "Extra", code: "EX", type: "Extra" },
];

/**
 * Deriva nome e código a partir da chave da vaga (o StationState não expõe nome/código).
 * - Chaves tipadas do design (elevador_1 → "Elevador 1" / "EL-01").
 * - Chaves genéricas do dado real (vaga_01 → "Vaga 01" / "V-01"); o tipo vem da API.
 */
export const deriveVagaMeta = (vagaKey: string): VagaMeta => {
  const key = (vagaKey ?? "").trim();
  const parsed = /^([a-zA-Z]+)[_-]?(\d+)?$/.exec(key);
  const prefix = parsed?.[1] ?? key;
  const num = parsed?.[2] ? parseInt(parsed[2], 10) : null;
  const numStr = num != null ? String(num).padStart(2, "0") : "";
  const found = PREFIXES.find((p) => p.match.test(prefix));

  if (found) {
    return {
      name: num != null ? `${found.name} ${num}` : found.name,
      code: num != null ? `${found.code}-${numStr}` : found.code,
      typeFromKey: found.type,
    };
  }

  return {
    name: num != null ? `Vaga ${numStr}` : key || "—",
    code: num != null ? `V-${numStr}` : (key || "—").toUpperCase(),
    typeFromKey: null,
  };
};

/**
 * Layout espacial fixo da estação (POC — vagas fixas no banco), definido pelo operador.
 * Um único grid de 12 colunas garante gaps uniformes e larguras consistentes:
 *  - Linha 1 (FUNDO): 2 elevadores (span 5 cada) + V-07 (span 2, lateral).
 *  - Linha 2 (FRENTE): 5 supermercado (span 2 cada) + V-06 (span 2, lateral).
 * Elevadores ficam mais largos; todo supermercado (frente e lateral) tem a mesma largura.
 */
export const FLOOR_GRID: Record<string, { gridColumn: string; gridRow: number }> = {
  // Linha 1 — Fundo (elevadores) + lateral superior
  vaga_08: { gridColumn: "1 / 6", gridRow: 1 },
  vaga_09: { gridColumn: "6 / 11", gridRow: 1 },
  vaga_07: { gridColumn: "11 / 13", gridRow: 1 },
  // Linha 2 — Frente (supermercado) + lateral inferior
  vaga_01: { gridColumn: "1 / 3", gridRow: 2 },
  vaga_03: { gridColumn: "3 / 5", gridRow: 2 },
  vaga_02: { gridColumn: "5 / 7", gridRow: 2 },
  vaga_04: { gridColumn: "7 / 9", gridRow: 2 },
  vaga_05: { gridColumn: "9 / 11", gridRow: 2 },
  vaga_06: { gridColumn: "11 / 13", gridRow: 2 },
};

/** Total de colunas do grid do piso. */
export const FLOOR_GRID_COLUMNS = 12;
